<template>
    <div class="table-answer-input" :class="{ 'is-mobile': mobile }">
        <div class="tai-scroll">
            <table class="tai-table" :style="maxWidth ? { maxWidth: maxWidth + 'px' } : null">
                <thead>
                    <tr>
                        <th v-if="hasRowLabels" class="tai-label-col"></th>
                        <th
                            v-for="(col, j) in columns"
                            :key="`h-${j}`"
                            :style="{ minWidth: minWidthOf(col) + 'px' }"
                        >
                            <!-- i18n-validator-ignore-next-line: runtime column label -->
                            {{ col.label }}<span v-if="col.required" class="tai-required">*</span>
                        </th>
                        <th v-if="isDynamic && editable" class="tai-actions-col"></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, i) in rowsValues" :key="`r-${i}`">
                        <th v-if="hasRowLabels" class="tai-label-col" scope="row">
                            <!-- i18n-validator-ignore-next-line: runtime row label -->
                            {{ isDynamic ? "" : itemLabel(i) }}
                        </th>
                        <td
                            v-for="(col, j) in columns"
                            :key="`c-${i}-${j}`"
                            :class="cellClass(i, j)"
                            :title="errorTitle(i + ':' + j)"
                        >
                            <span v-if="!editable || isReadOnlyCell(i, j)" class="tai-static">
                                {{ displayValue(i, j) }}
                            </span>
                            <select
                                v-else-if="col.type === 'select'"
                                class="tai-input"
                                :value="row[j] == null ? '' : row[j]"
                                @change="setValue(i, j, $event.target.value || null)"
                            >
                                <option value=""></option>
                                <option v-for="opt in optionsOf(col)" :key="opt.text" :value="opt.text">
                                    {{ opt.text }}
                                </option>
                            </select>
                            <input
                                v-else-if="col.type === 'date'"
                                type="date"
                                class="tai-input"
                                :value="row[j] || ''"
                                @input="setValue(i, j, $event.target.value || null)"
                            />
                            <input
                                v-else-if="col.type === 'number'"
                                type="text"
                                inputmode="decimal"
                                class="tai-input tai-number"
                                :value="numberText(i, j)"
                                @input="setNumber(i, j, $event.target.value)"
                                @blur="clearDraft(i, j)"
                            />
                            <input
                                v-else
                                type="text"
                                class="tai-input"
                                maxlength="500"
                                :value="row[j] == null ? '' : row[j]"
                                @input="setValue(i, j, $event.target.value)"
                            />
                        </td>
                        <td v-if="isDynamic && editable" class="tai-actions-col">
                            <button
                                type="button"
                                class="tai-remove"
                                :disabled="rowsValues.length <= 1"
                                :aria-label="t('forms-table-remove_row')"
                                @click="removeRow(i)"
                            >
                                <Icon icon-class="ui-trash-01" size="2" />
                            </button>
                        </td>
                    </tr>
                    <tr v-for="(s, k) in summaryDefs" :key="`s-${k}`" class="tai-summary">
                        <th v-if="hasRowLabels" class="tai-label-col" scope="row">
                            <!-- i18n-validator-ignore-next-line: runtime row label -->
                            {{ s.label }}
                        </th>
                        <td
                            v-for="(col, j) in columns"
                            :key="`s-${k}-${j}`"
                            class="is-formula"
                            :class="{ 'has-error': summaryErrors['s:' + k + ':' + j] }"
                            :title="summaryErrorTitle(k, j)"
                        >
                            <span class="tai-static">{{ summaryDisplay(k, j) }}</span>
                        </td>
                        <td v-if="isDynamic && editable" class="tai-actions-col"></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <button
            v-if="isDynamic && editable"
            type="button"
            class="tai-add"
            :disabled="rowsValues.length >= maxRows"
            @click="addRow"
        >
            <Icon icon-class="ui-plus" size="2" />
            <!-- i18n-validator-ignore-next-line: runtime button label -->
            {{ addLabel }}
        </button>
    </div>
</template>

<script>
import Icon from "../icon/icon";
import { computeTable, hasFormula, isRowFilled } from "../../helpers/tableFormula";
import {
    DEFAULT_DYNAMIC_MAX,
    activeOptions,
    defaultMinWidth,
    formatTableCell,
    isBlank,
    parseNumberInput,
} from "../../helpers/tableQuestion";

/*
  The table as the respondent fills it: editable cells, read-only formula and
  locked cells, live results. Used by the editor preview and to edit answers.
  `settings` holds {columns, rows}; v-model is the list of data rows.
*/
export default {
    name: "TableAnswerInput",
    components: { Icon },
    props: {
        settings: { type: Object, required: true },
        value: { type: Array, default: null },
        editable: { type: Boolean, default: true },
        mobile: { type: Boolean, default: false },
    },
    data() {
        return {
            rowsValues: [],
            drafts: {},
        };
    },
    computed: {
        columns() {
            return this.settings.columns || [];
        },
        rowsDef() {
            return this.settings.rows || {};
        },
        isDynamic() {
            return this.rowsDef.mode !== "fixed";
        },
        summaryDefs() {
            return this.rowsDef.summary || [];
        },
        hasRowLabels() {
            return !this.isDynamic || this.summaryDefs.length > 0;
        },
        maxRows() {
            return Number(this.rowsDef.max) || DEFAULT_DYNAMIC_MAX;
        },
        maxWidth() {
            return Number(this.settings.maxWidth) || null;
        },
        addLabel() {
            return this.rowsDef.addLabel || this.t("forms-table-add_row_default");
        },
        // Per-row formulas see every shown row; summaries only the filled ones,
        // the same rows the server keeps.
        computedRows() {
            return this.safeCompute(this.rowsValues);
        },
        filledRows() {
            if (!this.isDynamic) return this.rowsValues;
            return this.rowsValues.filter((row) => isRowFilled(this.settings, row));
        },
        computedSummary() {
            return this.safeCompute(this.filledRows);
        },
        summaryErrors() {
            return this.computedSummary.errors || {};
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (value && value === this.lastEmitted) return;
                this.rowsValues = this.initialRows(value);
            },
        },
        settings: {
            deep: true,
            handler() {
                this.rowsValues = this.initialRows(this.rowsValues);
            },
        },
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        minWidthOf(col) {
            return Number(col.minWidth) || defaultMinWidth(col);
        },
        optionsOf(col) {
            return activeOptions(col);
        },
        itemLabel(i) {
            return ((this.rowsDef.items || [])[i] || {}).label || "";
        },
        cfgAt(i, j) {
            const cells = this.isDynamic
                ? ((this.rowsDef.template || {}).cells || [])
                : (((this.rowsDef.items || [])[i] || {}).cells || []);
            return cells[j] || null;
        },
        defaultRow(i) {
            return this.columns.map((col, j) => {
                const cfg = this.cfgAt(i, j);
                return cfg && !isBlank(cfg.value) && !hasFormula(cfg) ? cfg.value : null;
            });
        },
        // Fits the given rows to the current shape (columns, fixed items).
        initialRows(source) {
            const n = this.columns.length;
            const fit = (row) => {
                const out = [];
                for (let j = 0; j < n; j++) {
                    out.push(row && row[j] !== undefined ? row[j] : null);
                }
                return out;
            };
            if (!this.isDynamic) {
                const items = this.rowsDef.items || [];
                return items.map((item, i) => {
                    const given = source && source[i];
                    return given ? fit(given) : this.defaultRow(i);
                });
            }
            if (source && source.length) return source.map((row) => fit(row));
            const count = Math.max(1, Number(this.rowsDef.min) || 0);
            return Array.from({ length: Math.min(count, this.maxRows) }, () => this.defaultRow(0));
        },
        safeCompute(rows) {
            try {
                return computeTable(this.settings, rows);
            } catch (e) {
                return { values: rows, summary: [], errors: {} };
            }
        },
        isReadOnlyCell(i, j) {
            const cfg = this.cfgAt(i, j);
            return !!(cfg && (cfg.locked || hasFormula(cfg)));
        },
        cellClass(i, j) {
            const cfg = this.cfgAt(i, j);
            return {
                "is-formula": hasFormula(cfg),
                "is-locked": !!(cfg && cfg.locked && !hasFormula(cfg)),
                "has-error": !!(this.computedRows.errors || {})[i + ":" + j],
            };
        },
        displayValue(i, j) {
            const values = this.computedRows.values || [];
            const v = values[i] ? values[i][j] : this.rowsValues[i][j];
            return formatTableCell(v, this.columns[j]);
        },
        summaryDisplay(k, j) {
            const line = (this.computedSummary.summary || [])[k] || [];
            return formatTableCell(line[j], this.columns[j]);
        },
        errorTitle(key) {
            const code = (this.computedRows.errors || {})[key];
            return code ? this.t("forms-table-formula_error", [code]) : null;
        },
        summaryErrorTitle(k, j) {
            const code = this.summaryErrors["s:" + k + ":" + j];
            return code ? this.t("forms-table-formula_error", [code]) : null;
        },
        numberText(i, j) {
            const key = i + ":" + j;
            if (Object.prototype.hasOwnProperty.call(this.drafts, key)) return this.drafts[key];
            const v = this.rowsValues[i][j];
            return v == null ? "" : String(v).replace(".", ",");
        },
        setNumber(i, j, text) {
            this.$set(this.drafts, i + ":" + j, text);
            const parsed = parseNumberInput(text);
            this.setValue(i, j, parsed === null ? (String(text).trim() ? text : null) : parsed);
        },
        clearDraft(i, j) {
            this.$delete(this.drafts, i + ":" + j);
        },
        setValue(i, j, value) {
            const row = this.rowsValues[i].slice();
            row[j] = value;
            this.rowsValues.splice(i, 1, row);
            this.emit();
        },
        addRow() {
            if (this.rowsValues.length >= this.maxRows) return;
            this.rowsValues.push(this.defaultRow(0));
            this.emit();
        },
        removeRow(i) {
            this.rowsValues.splice(i, 1);
            this.drafts = {};
            this.emit();
        },
        emit() {
            this.lastEmitted = this.rowsValues.map((row) => row.slice());
            this.$emit("input", this.lastEmitted);
        },
    },
};
</script>

<style scoped lang="less">
.table-answer-input {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;

    &.is-mobile {
        max-width: 375px;
    }
}

.tai-scroll {
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fff;
}

.tai-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th,
    td {
        border-bottom: 1px solid #f3f4f6;
        border-right: 1px solid #f3f4f6;
        padding: 0;
        height: 34px;
        vertical-align: middle;
    }

    thead th {
        padding: 6px 8px;
        background: #f9fafb;
        color: #4d5761;
        font-weight: 500;
        text-align: left;
        white-space: nowrap;
    }

    .tai-label-col {
        padding: 6px 8px;
        min-width: 120px;
        background: #f9fafb;
        color: #384250;
        font-weight: 500;
        text-align: left;
    }

    .tai-actions-col {
        width: 36px;
        min-width: 36px;
        text-align: center;
    }

    td.is-formula,
    td.is-locked {
        background: #f6f8fa;
    }

    td.is-formula .tai-static {
        color: #0b6b3a;
        font-variant-numeric: tabular-nums;
    }

    td.has-error {
        background: #fef3f2;
    }

    .tai-summary th,
    .tai-summary td {
        border-top: 1px solid #e5e7eb;
        font-weight: 500;
    }
}

.tai-required {
    color: #d92d20;
    margin-left: 2px;
}

.tai-static {
    display: block;
    padding: 6px 8px;
    white-space: nowrap;
}

.tai-input {
    width: 100%;
    height: 34px;
    border: 0;
    padding: 6px 8px;
    background: transparent;
    font-size: 13px;
    color: #111927;

    &:focus {
        outline: 2px solid #2aaa4c;
        outline-offset: -2px;
        background: #fff;
    }
}

.tai-number {
    text-align: right;
    font-variant-numeric: tabular-nums;
}

.tai-remove {
    border: 0;
    background: transparent;
    color: #9da4ae;
    padding: 4px;

    &:hover:not(:disabled) {
        color: #d92d20;
    }

    &:disabled {
        opacity: 0.4;
    }
}

.tai-add {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px dashed #d2d6db;
    border-radius: 8px;
    background: #fff;
    padding: 6px 12px;
    font-size: 13px;
    color: #384250;

    &:disabled {
        opacity: 0.5;
    }
}
</style>
